import { Add, Edit } from "@mui/icons-material";
import {
    Button,
    IconButton,
    Switch,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem
} from "@mui/material";
import { useFormik } from "formik";
import moment from "moment";
import { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { API_URLS } from "../../config/APIUrls";
import axiosInstance from "../../config/axios";
import CustomFilter from "../../Shared/CustomForFiler";
import CustomTable from "../../Shared/CustomTable";
import CustomToPagination from "../../Shared/CustomToPagination";
import { TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import dayjs from 'dayjs';

const weekdays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const ShiftMaster = () => {

    const queryClient = useQueryClient();
    const [open, setOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const [page, setPage] = useState(1);

    // FILTER
    const filterFormik = useFormik({
        initialValues: { search: "", start_date: "", end_date: "" },
        enableReinitialize: true,
    });

    // FETCH SHIFTS
    const { data: shiftData, isLoading: loading } = useQuery(
        ["get_shift", page, filterFormik.values],
        () =>
            axiosInstance.get(API_URLS.get_shift, {
                params: { ...filterFormik.values, page, count: 10 },
            }),
        { refetchOnWindowFocus: false }
    );

    const shiftList = shiftData?.data?.response || [];

    // FORM
    const fk = useFormik({
        initialValues: {
            name: "",
            duration_h: 0,
            duration_m: 0,
            str_grace: "0",
            str_grace_bef: false,
            str_grace_aft: false,
            end_grace: "0",
            end_grace_bef: false,
            end_grace_aft: false,
            week1: "",
            week2: "",
            status: "1",
            m07_sh_start_time: ""
        },

        validate: (values) => {
            const errors = {};

            if (!values.name) {
                errors.name = "Please select name";
            }
            // Duration check
            if (values.duration_h === 0 && values.duration_m === 0) {
                errors.duration = "Please select duration";
            }

            if (!values.m07_sh_start_time) {
                errors.m07_sh_start_time = "Start time required";
            }

            return errors;
        },

        onSubmit: (values) => {
            const formattedValues = {
                ...values,
                m07_sh_start_time: moment(values.m07_sh_start_time, ["HH:mm", "h:mm A"]).format("HH:mm")
            };
            if (editData) return updateShift(formattedValues);
            return createShift(formattedValues);
        },

    });

    // CREATE SHIFT
    const createShift = async (v) => {
        try {
            const reqBody = {
                m07_sh_name: v.name,
                m07_sh_duration_h: v.duration_h,
                m07_sh_duration_m: v.duration_m,
                m07_sh_str_grace: v.str_grace || 0,
                m07_sh_str_grace_bef: v.str_grace_bef ? 1 : 0,
                m07_sh_str_grace_aft: v.str_grace_aft ? 1 : 0,
                m07_sh_end_grace: v.end_grace || 0,
                m07_sh_end_grace_bef: v.end_grace_bef ? 1 : 0,
                m07_sh_end_grace_aft: v.end_grace_aft ? 1 : 0,
                m07_sh_week_off_01: v.week1,
                m07_sh_week_off_02: v.week2,
                m07_sh_status: 1,
                m07_sh_start_time: v.m07_sh_start_time
            };

            const res = await axiosInstance.post(API_URLS.create_shift, reqBody);
            toast(res?.data?.msg);

            if (res?.data?.success) {
                queryClient.invalidateQueries("get_shift");
                handleClose();
            }
        } catch {
            toast.error("Failed to create shift");
        }
    };

    // UPDATE SHIFT
    const updateShift = async (v) => {
        try {
            const reqBody = {
                m07_sh_id: editData.m07_sh_id,
                m07_sh_name: v.name,
                m07_sh_duration_h: v.duration_h,
                m07_sh_duration_m: v.duration_m,
                m07_sh_str_grace: v.str_grace,
                m07_sh_str_grace_bef: v.str_grace_bef ? 1 : 0,
                m07_sh_str_grace_aft: v.str_grace_aft ? 1 : 0,
                m07_sh_end_grace: v.end_grace,
                m07_sh_end_grace_bef: v.end_grace_bef ? 1 : 0,
                m07_sh_end_grace_aft: v.end_grace_aft ? 1 : 0,
                m07_sh_week_off_01: v.week1,
                m07_sh_week_off_02: v.week2,
                m07_sh_status: v.status,
                m07_sh_start_time: v.m07_sh_start_time
            };

            const res = await axiosInstance.post(API_URLS.update_shift, reqBody);
            toast(res?.data?.msg);

            if (res?.data?.success) {
                queryClient.invalidateQueries("get_shift");
                handleClose();
            }
        } catch {
            toast.error("Failed to update shift");
        }
    };

    const toggleStatus = async (row) => {
        try {
            const res = await axiosInstance.post(API_URLS.status_shift, {
                m07_sh_id: row.m07_sh_id,
            });

            toast(res?.data?.msg);
            if (res?.data?.success) queryClient.invalidateQueries("get_shift");

        } catch {
            toast.error("Failed to update status");
        }
    };

    // EDIT
    const handleEdit = (row) => {
        setEditData(row);

        fk.setValues({
            name: row.m07_sh_name,
            duration_h: row.m07_sh_duration_h,
            duration_m: row.m07_sh_duration_m,
            str_grace: row.m07_sh_str_grace,
            str_grace_bef: row.m07_sh_str_grace_bef === 1,
            str_grace_aft: row.m07_sh_str_grace_aft === 1,
            end_grace: row.m07_sh_end_grace,
            end_grace_bef: row.m07_sh_end_grace_bef === 1,
            end_grace_aft: row.m07_sh_end_grace_aft === 1,
            week1: row.m07_sh_week_off_01 || "",
            week2: row.m07_sh_week_off_02 || "",
            status: row.m07_sh_status,
            m07_sh_start_time: row.m07_sh_start_time
        });

        setOpen(true);
    };

   const handleExport = async () => {
        try {
            const res = await axiosInstance.get(API_URLS.shift_excel, {
                search: filterFormik.values.search || "",
                start_date: filterFormik.values.start_date || "",
                end_date: filterFormik.values.end_date || "",
            })
            const fileUrl = res?.data?.response?.url;
            window.open(fileUrl, "_blank")
        }
        catch (e) {
            console.log("Something went wrong")
        }
    }

    const handleOpen = () => {
        fk.resetForm();
        setEditData(null);
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    const tableHead = [
        "S.No",
        "Shift Name",
        "Start Time",
        "End Time",
        "Duration",
        "Week Offs",
        "Status",
        "Actions",
        "Created"
    ];

    const tableRows = shiftList?.data?.map((item, i) => [
        i + 1,
        item.m07_sh_name,
        item.m07_sh_start_time
            ? moment(item.m07_sh_start_time, "HH:mm:ss").format("HH:mm")
            : "--",
        item.m07_sh_end_time
            ? moment(item.m07_sh_end_time, "HH:mm:ss").format("HH:mm")
            : "--",
        `${item.m07_sh_duration_h}h ${item.m07_sh_duration_m}m`,
        item.m07_sh_week_off_01 || item.m07_sh_week_off_02
            ? `${item.m07_sh_week_off_01 || ""} ${item.m07_sh_week_off_02 || ""}`.trim()
            : "--",
        <Switch
            checked={item.m07_sh_status === 1}
            onChange={() => toggleStatus(item)}
            color="success"
        />,
        <Tooltip title="Edit">
            <IconButton onClick={() => handleEdit(item)}>
                <Edit className="text-blue-600" />
            </IconButton>
        </Tooltip>,
        moment(item.m07_sh_created_at).format("DD-MM-YYYY")
    ]);


    return (
        <div className="mx-5">
            <div className="flex justify-between items-center p-1 mb-5" >
                <p className="font-bold text-xl">Shift Master</p>
                  <Button variant="contained" className="!bg-[#00008b]"  startIcon={<Add />} onClick={handleOpen}>
                    Add 
                </Button>
            </div>

            <CustomFilter
                formik={filterFormik}
                onFilter={() => {
                    setPage(1);
                    queryClient.invalidateQueries("get_shift");
                }}
                onClear={() => {
                    filterFormik.resetForm();
                    setPage(1);
                    queryClient.invalidateQueries("get_shift");
                }}
                onExport={handleExport}
            />

            <CustomTable tablehead={tableHead} tablerow={tableRows} isLoading={loading} />
            <CustomToPagination setPage={setPage} page={page} data={shiftList} />

            {/* DIALOG */}
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle>{editData ? "Edit Shift" : "Add Shift"}</DialogTitle>

                <DialogContent>
                    <div className="grid grid-cols-2 gap-4 mt-2">

                        <div className="flex flex-col">
                            <label>Shift Name</label>
                            <TextField
                                label="Shift Name"
                                name="name"
                                size="small"
                                fullWidth
                                value={fk.values.name}
                                onChange={fk.handleChange}

                            />
                            {fk.errors.name && (
                                <span className="text-red-600 text-sm">{fk.errors.name}</span>
                            )}
                        </div>


                        <div className="flex justify-between">


                            {/* START TIME */}
                            <div className="flex flex-col !w-40">
                                <label>Start Time</label>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <TimePicker
                                        value={fk.values.m07_sh_start_time ? dayjs(`1970-01-01T${fk.values.m07_sh_start_time}`) : null}
                                        onChange={(newValue) => {
                                            const formattedTime = newValue ? dayjs(newValue).format("HH:mm") : "";
                                            fk.setFieldValue("m07_sh_start_time", formattedTime);
                                        }}
                                        viewRenderers={{
                                            hours: renderTimeViewClock,
                                            minutes: renderTimeViewClock,
                                        }}
                                        ampm={false} // 24-hour format
                                        views={['hours', 'minutes']} // show only hours and minutes
                                    />
                                </LocalizationProvider>

                                {fk.errors.m07_sh_start_time && (
                                    <span className="text-red-600 text-sm">{fk.errors.m07_sh_start_time}</span>
                                )}
                            </div>


                            {/* DURATION */}
                            <div className="flex flex-col">
                                <label className="text-sm mb-1 font-semibold">Duration</label>

                                <div className="grid grid-cols-2 gap-3">
                                    <TextField
                                        select
                                        label="Hours"
                                        name="duration_h"
                                        size="small"
                                        value={fk.values.duration_h}
                                        onChange={fk.handleChange}
                                        error={Boolean(fk.errors.duration)}
                                    >
                                        {[...Array(25)].map((_, i) => (
                                            <MenuItem key={i} value={i}>{i} hr</MenuItem>
                                        ))}
                                    </TextField>

                                    <TextField
                                        select
                                        label="Minutes"
                                        name="duration_m"
                                        size="small"
                                        value={fk.values.duration_m}
                                        onChange={fk.handleChange}
                                        error={Boolean(fk.errors.duration)}
                                    >
                                        {[...Array(61)].map((_, i) => (
                                            <MenuItem key={i} value={i}>{i} min</MenuItem>
                                        ))}
                                    </TextField>
                                </div>

                                {fk.errors.duration && (
                                    <span className="text-red-600 text-sm mt-1">{fk.errors.duration}</span>
                                )}
                            </div>
                        </div>

                        {/* START GRACE */}
                        <div className="flex flex-col">
                            <TextField
                                label="Start Grace (minutes)"
                                name="str_grace"
                                type="number"
                                size="small"
                                value={fk.values.str_grace}
                                onChange={fk.handleChange}
                            />

                            <div className="flex items-center gap-6 mt-2 ml-1">
                                <label className="flex items-center gap-1">
                                    <input type="checkbox"
                                        checked={fk.values.str_grace_bef}
                                        onChange={(e) => fk.setFieldValue("str_grace_bef", e.target.checked)}
                                    />
                                    <span>Before</span>
                                </label>

                                <label className="flex items-center gap-1">
                                    <input type="checkbox"
                                        checked={fk.values.str_grace_aft}
                                        onChange={(e) => fk.setFieldValue("str_grace_aft", e.target.checked)}
                                    />
                                    <span>After</span>
                                </label>
                            </div>
                        </div>

                        {/* END GRACE */}
                        <div className="flex flex-col">
                            <TextField
                                label="End Grace (minutes)"
                                name="end_grace"
                                type="number"
                                size="small"
                                value={fk.values.end_grace}
                                onChange={fk.handleChange}
                            />

                            <div className="flex items-center gap-6 mt-2 ml-1">
                                <label className="flex items-center gap-1">
                                    <input type="checkbox"
                                        checked={fk.values.end_grace_bef}
                                        onChange={(e) => fk.setFieldValue("end_grace_bef", e.target.checked)}
                                    />
                                    <span>Before</span>
                                </label>

                                <label className="flex items-center gap-1">
                                    <input type="checkbox"
                                        checked={fk.values.end_grace_aft}
                                        onChange={(e) => fk.setFieldValue("end_grace_aft", e.target.checked)}
                                    />
                                    <span>After</span>
                                </label>
                            </div>
                        </div>

                        {/* WEEK OFF 1 */}
                        <TextField
                            label="Week Off 1"
                            name="week1"
                            select
                            size="small"
                            value={fk.values.week1}
                            onChange={fk.handleChange}
                        >
                            <MenuItem value="">Select</MenuItem>
                            {weekdays.map((w) => (
                                <MenuItem key={w} value={w}>{w}</MenuItem>
                            ))}
                        </TextField>

                        {/* WEEK OFF 2 */}
                        <TextField
                            label="Week Off 2"
                            name="week2"
                            select
                            size="small"
                            value={fk.values.week2}
                            onChange={fk.handleChange}
                        >
                            <MenuItem value="">Select</MenuItem>
                            {weekdays.map((w) => (
                                <MenuItem key={w} value={w}>{w}</MenuItem>
                            ))}
                        </TextField>

                    </div>
                </DialogContent>

                <DialogActions>
                    <Button variant="outlined" onClick={handleClose}>Cancel</Button>
                    <Button variant="contained" onClick={fk.handleSubmit}>
                        {editData ? "Update" : "Create"}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default ShiftMaster;
